import { z } from 'zod';

import { CompanyId, CustodialAccountId, PoolId, PoolInternalId } from './BrandedIds';
import { RemittanceType } from './RemittanceType';
import { FIVE_TO_SEVENTEEN_DIGITS } from './utils/bankNumberPatterns';
import { zodDateOrString } from './utils/Zod';

export const PoolStatus = z.enum(['PENDING_ACTIVATION', 'ACTIVE']);
export type PoolStatus = z.infer<typeof PoolStatus>;

// Single-family GNMA pool types only. Multifamily codes (CL/CS/LM/LS/PL/PN/RX)
// are intentionally excluded — this app only services single-family pools.
export const PoolType = z.enum([
  'SF',
  'MH',
  'BD',
  'ET',
  'RG',
  'GP',
  'GD',
  'GA',
  'GT',
  'AF',
  'AQ',
  'AR',
  'AS',
  'AT',
  'AX',
  'FB',
  'FL',
  'FS',
  'FT',
  'JM',
  'QL',
  'RL',
  'SL',
  'SN',
  'TL',
  'XL',
]);
export type PoolType = z.infer<typeof PoolType>;

const FOUR_DIGITS = /^\d{4}$/;
const SIX_DIGITS = /^\d{6}$/;

// Buffer is a Node global, undefined in browsers. The runtime check stays safe
// in both environments and is a no-op in the browser, where these fields are
// never populated anyway (encrypted blobs never flow to the client).
const isBufferOrAbsent = (v: unknown): boolean => v == null || (typeof Buffer !== 'undefined' && Buffer.isBuffer(v));

export const Pool = z.object({
  id: PoolInternalId,
  poolId: PoolId,
  companyId: CompanyId,
  status: PoolStatus,
  investor: z.string().optional(),
  remittanceType: RemittanceType.optional(),

  clearingAccountLastFour: z.string().optional(),
  principalAndInterestAccountLastFour: z.string().optional(),
  taxAndInsuranceAccountLastFour: z.string().optional(),
  servicingFeesAccountLastFour: z.string().optional(),
  suspenseAccountLastFour: z.string().optional(),
  guaranteeFeesAccountLastFour: z.string().optional(),
  guaranteeFeesAccountNotApplicable: z.boolean(),

  principalAndInterestBankName: z.string().optional(),
  principalAndInterestAccountNumber: z.custom<Buffer>(isBufferOrAbsent).optional(),
  principalAndInterestRoutingNumber: z.string().optional(),
  taxAndInsuranceBankName: z.string().optional(),
  taxAndInsuranceAccountNumber: z.custom<Buffer>(isBufferOrAbsent).optional(),
  taxAndInsuranceRoutingNumber: z.string().optional(),

  // GNMA pools reference custodial accounts by FK per spec role (see refineGnmaPoolFields).
  principalAndInterestCustodialAccountId: CustodialAccountId.optional(),
  taxAndInsuranceCustodialAccountId: CustodialAccountId.optional(),
  clearingCustodialAccountId: CustodialAccountId.optional(),
  servicingFeesCustodialAccountId: CustodialAccountId.optional(),
  suspenseCustodialAccountId: CustodialAccountId.optional(),
  guaranteeFeesCustodialAccountId: CustodialAccountId.optional(),

  gnmaIssuerId: z.string().optional(),
  securityInterestRate: z.number().optional(),
  poolType: PoolType.optional(),
  documentCustodianId: z.string().optional(),
  issueDate: zodDateOrString.optional(),

  createdAt: zodDateOrString,
  updatedAt: zodDateOrString,
});
export type Pool = z.infer<typeof Pool>;

// Shape consumed by `refineGnmaPoolFields`. Loose by design so both the strict
// (PoolInputComplete) and permissive (PoolInputNullable) schemas can pass.
type GnmaPoolRefineInput = {
  investor?: string | null;
  gnmaIssuerId?: string | null;
  securityInterestRate?: number | null;
  poolType?: PoolType | null;
  documentCustodianId?: string | null;
  issueDate?: Date | string | null;
  principalAndInterestCustodialAccountId?: string | null;
  taxAndInsuranceCustodialAccountId?: string | null;
};

// GNMA pools hold account details on referenced custodial accounts rather than inline columns,
// so completeness requires the GNMA-specific fields plus the P&I and T&I account references.
export const refineGnmaPoolFields = (data: GnmaPoolRefineInput, ctx: z.RefinementCtx) => {
  if (data.investor !== 'GNMA') return;

  const required = (path: string, message = 'Required') => {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: [path] });
  };

  if (!data.gnmaIssuerId || !FOUR_DIGITS.test(data.gnmaIssuerId)) {
    required('gnmaIssuerId', 'Must be 4 digits');
  }
  if (
    data.securityInterestRate == null ||
    Number.isNaN(data.securityInterestRate) ||
    data.securityInterestRate < 0 ||
    data.securityInterestRate > 100
  ) {
    required('securityInterestRate', 'Must be between 0 and 100');
  }
  if (!data.poolType) required('poolType');
  if (!data.documentCustodianId || !SIX_DIGITS.test(data.documentCustodianId)) {
    required('documentCustodianId', 'Must be 6 digits');
  }
  if (!data.issueDate || Number.isNaN(new Date(data.issueDate).getTime())) {
    required('issueDate');
  }

  if (!data.principalAndInterestCustodialAccountId) required('principalAndInterestCustodialAccountId');
  if (!data.taxAndInsuranceCustodialAccountId) required('taxAndInsuranceCustodialAccountId');
};

// Shape consumed by `refineNonGnmaPoolFields`.
type NonGnmaPoolRefineInput = {
  investor?: string | null;
  clearingAccountLastFour?: string | null;
  principalAndInterestAccountLastFour?: string | null;
  taxAndInsuranceAccountLastFour?: string | null;
  servicingFeesAccountLastFour?: string | null;
  suspenseAccountLastFour?: string | null;
  guaranteeFeesAccountLastFour?: string | null;
  guaranteeFeesAccountNotApplicable?: boolean | null;
};

// Non-GNMA pools store account info inline; completeness requires the last-four of each servicing
// account (and the guarantee-fees account unless marked not applicable).
export const refineNonGnmaPoolFields = (data: NonGnmaPoolRefineInput, ctx: z.RefinementCtx) => {
  if (data.investor === 'GNMA') return;

  const requireLastFour = (path: string, value?: string | null) => {
    if (!value || value.length !== 4) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required', path: [path] });
    }
  };

  requireLastFour('clearingAccountLastFour', data.clearingAccountLastFour);
  requireLastFour('principalAndInterestAccountLastFour', data.principalAndInterestAccountLastFour);
  requireLastFour('taxAndInsuranceAccountLastFour', data.taxAndInsuranceAccountLastFour);
  requireLastFour('servicingFeesAccountLastFour', data.servicingFeesAccountLastFour);
  requireLastFour('suspenseAccountLastFour', data.suspenseAccountLastFour);

  if (data.guaranteeFeesAccountNotApplicable !== true) {
    requireLastFour('guaranteeFeesAccountLastFour', data.guaranteeFeesAccountLastFour);
  }
};

export const PoolInputComplete = z
  .object({
    poolId: PoolId,
    investor: z.string().min(1),
    remittanceType: RemittanceType,
    // Last-fours are optional at the base layer; refineNonGnmaPoolFields requires them for
    // non-GNMA pools, while GNMA pools carry account info on referenced custodial accounts.
    clearingAccountLastFour: z.string().length(4).or(z.literal('')).nullable().optional(),
    principalAndInterestAccountLastFour: z.string().length(4).or(z.literal('')).nullable().optional(),
    taxAndInsuranceAccountLastFour: z.string().length(4).or(z.literal('')).nullable().optional(),
    servicingFeesAccountLastFour: z.string().length(4).or(z.literal('')).nullable().optional(),
    suspenseAccountLastFour: z.string().length(4).or(z.literal('')).nullable().optional(),
    guaranteeFeesAccountLastFour: z.string().length(4).or(z.literal('')).nullable().optional(),
    guaranteeFeesAccountNotApplicable: z.boolean(),

    principalAndInterestBankName: z.string().min(1).nullable().optional(),
    principalAndInterestAccountNumber: z.string().regex(FIVE_TO_SEVENTEEN_DIGITS).nullable().optional(),
    principalAndInterestRoutingNumber: z.string().nullable().optional(),
    taxAndInsuranceBankName: z.string().min(1).nullable().optional(),
    taxAndInsuranceAccountNumber: z.string().regex(FIVE_TO_SEVENTEEN_DIGITS).nullable().optional(),
    taxAndInsuranceRoutingNumber: z.string().nullable().optional(),

    principalAndInterestCustodialAccountId: CustodialAccountId.nullable().optional(),
    taxAndInsuranceCustodialAccountId: CustodialAccountId.nullable().optional(),
    clearingCustodialAccountId: CustodialAccountId.nullable().optional(),
    servicingFeesCustodialAccountId: CustodialAccountId.nullable().optional(),
    suspenseCustodialAccountId: CustodialAccountId.nullable().optional(),
    guaranteeFeesCustodialAccountId: CustodialAccountId.nullable().optional(),

    gnmaIssuerId: z.string().nullable().optional(),
    securityInterestRate: z.number().nullable().optional(),
    poolType: PoolType.nullable().optional(),
    documentCustodianId: z.string().nullable().optional(),
    issueDate: zodDateOrString.nullable().optional(),
  })
  .superRefine((data, ctx) => {
    // Each refiner no-ops for the other investor kind, so the active one decides completeness.
    refineGnmaPoolFields(data, ctx);
    refineNonGnmaPoolFields(data, ctx);
  });

export const PoolInputNullable = z.object({
  poolId: PoolId,
  status: PoolStatus,
  investor: z.string().nullable().optional(),
  remittanceType: RemittanceType.nullable().optional(),
  clearingAccountLastFour: z.string().length(4).or(z.literal('')).nullable().optional(),
  principalAndInterestAccountLastFour: z.string().length(4).or(z.literal('')).nullable().optional(),
  taxAndInsuranceAccountLastFour: z.string().length(4).or(z.literal('')).nullable().optional(),
  servicingFeesAccountLastFour: z.string().length(4).or(z.literal('')).nullable().optional(),
  suspenseAccountLastFour: z.string().length(4).or(z.literal('')).nullable().optional(),
  guaranteeFeesAccountLastFour: z.string().length(4).or(z.literal('')).nullable().optional(),
  guaranteeFeesAccountNotApplicable: z.boolean(),

  principalAndInterestBankName: z.string().nullable().optional(),
  principalAndInterestAccountNumber: z.string().nullable().optional(),
  principalAndInterestRoutingNumber: z.string().nullable().optional(),
  taxAndInsuranceBankName: z.string().nullable().optional(),
  taxAndInsuranceAccountNumber: z.string().nullable().optional(),
  taxAndInsuranceRoutingNumber: z.string().nullable().optional(),

  principalAndInterestCustodialAccountId: CustodialAccountId.nullable().optional(),
  taxAndInsuranceCustodialAccountId: CustodialAccountId.nullable().optional(),
  clearingCustodialAccountId: CustodialAccountId.nullable().optional(),
  servicingFeesCustodialAccountId: CustodialAccountId.nullable().optional(),
  suspenseCustodialAccountId: CustodialAccountId.nullable().optional(),
  guaranteeFeesCustodialAccountId: CustodialAccountId.nullable().optional(),

  gnmaIssuerId: z.string().nullable().optional(),
  securityInterestRate: z.number().nullable().optional(),
  poolType: PoolType.nullable().optional(),
  documentCustodianId: z.string().nullable().optional(),
  issueDate: zodDateOrString.nullable().optional(),
});
export type PoolInputNullable = z.infer<typeof PoolInputNullable>;
